import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CalendarDays,
  Copy,
  Download,
  History,
  Loader2,
  PlayCircle,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import { RunStatusPill } from "@/pages/DemoRunsListPage";
import { formatRunNumber } from "@/lib/runNumber";
import FolderCountBadge, { useFolderSummary } from "@/components/FolderCountBadge";
import AddToFolderDropdown from "@/components/AddToFolderDropdown";

const STATUS_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "running", label: "Running" },
  { value: "completed", label: "Completed" },
  { value: "failed", label: "Failed" },
  { value: "cancelled", label: "Cancelled" },
];

const DATE_PRESETS = [
  { value: "all", label: "Any date" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

function isoSinceForPreset(preset) {
  const now = new Date();
  if (preset === "today") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return start.toISOString();
  }
  if (preset === "7d") {
    return new Date(now.getTime() - 7 * 86400000).toISOString();
  }
  if (preset === "30d") {
    return new Date(now.getTime() - 30 * 86400000).toISOString();
  }
  return null;
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function formatDuration(ms) {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

const TAG_CHOICES = [
  "procurement",
  "approvals",
  "requisitions",
  "invoices",
  "ap-automation",
  "vendors",
  "demo",
];

export default function DemoHistoryPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const canRun = hasPermission("workflow.run");
  const canDuplicate = hasPermission("workflow.write");

  const [statusFilter, setStatusFilter] = useState("all");
  const [workflowFilter, setWorkflowFilter] = useState("all");
  const [emailFilter, setEmailFilter] = useState("");
  const [datePreset, setDatePreset] = useState("all");
  const [tagFilter, setTagFilter] = useState("all");
  const [groupByWorkflow, setGroupByWorkflow] = useState(false);

  const workflows = useQuery({
    queryKey: ["workflows", "history-page", "all"],
    queryFn: async () =>
      (await api.get("/workflows", { params: { status: "all", page_size: 200 } })).data,
    staleTime: 60_000,
  });

  const runs = useQuery({
    queryKey: [
      "demo-runs",
      "history",
      statusFilter,
      workflowFilter,
      emailFilter,
      datePreset,
    ],
    queryFn: async () => {
      const params = { page_size: 100 };
      if (statusFilter !== "all") params.status = statusFilter;
      if (workflowFilter !== "all") params.workflow_id = workflowFilter;
      if (emailFilter.trim()) params.triggered_by = emailFilter.trim();
      const since = isoSinceForPreset(datePreset);
      if (since) params.since = since;
      return (await api.get("/demo-runs", { params })).data;
    },
    refetchInterval: 30_000,
  });

  // Tag filter happens client-side because demo_runs doesn't store tags;
  // we filter via the matched workflow's tag list.
  const wfTagsById = useMemo(() => {
    const m = new Map();
    (workflows.data?.items || []).forEach((w) =>
      m.set(w.workflow_id, w.tags || []),
    );
    return m;
  }, [workflows.data]);

  const filteredRuns = useMemo(() => {
    const items = runs.data?.items || [];
    if (tagFilter === "all") return items;
    return items.filter((r) =>
      (wfTagsById.get(r.workflow_id) || []).includes(tagFilter),
    );
  }, [runs.data, tagFilter, wfTagsById]);

  const grouped = useMemo(() => {
    if (!groupByWorkflow) return null;
    const groups = new Map();
    for (const r of filteredRuns) {
      const key = r.workflow_id;
      if (!groups.has(key)) {
        groups.set(key, {
          workflow_id: key,
          workflow_name: r.workflow_name_snapshot,
          rows: [],
        });
      }
      groups.get(key).rows.push(r);
    }
    return Array.from(groups.values());
  }, [groupByWorkflow, filteredRuns]);

  const rerunMutation = useMutation({
    mutationFn: async (runId) =>
      (await api.post(`/demo-runs/${runId}/rerun`)).data,
    onSuccess: (run) => {
      toast.success(`Rerun started (pinned to v${run.workflow_version})`);
      navigate(`/runs/${run.run_id}`);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  const duplicateMutation = useMutation({
    mutationFn: async (runId) =>
      (await api.post(`/demo-runs/${runId}/duplicate-as-new`)).data,
    onSuccess: (wf) => {
      toast.success(`Cloned to new workflow: ${wf.slug}`);
      queryClient.invalidateQueries({ queryKey: ["workflows"] });
      navigate(`/workflows/${wf.workflow_id}/edit`);
    },
    onError: (err) => toast.error(formatApiError(err)),
  });

  async function handleExport(runId) {
    try {
      const res = await api.get(`/demo-runs/${runId}/export`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `eyvo-demo-run-${runId.slice(0, 8)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="space-y-6" data-testid="history-page">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <History size={18} className="text-indigo-600" />
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
              Demo History
            </h1>
          </div>
          <p className="mt-1 max-w-2xl text-sm text-slate-600">
            All-time roster of demo runs across every workflow, with filters,
            rerun, duplicate-as-new, and JSON export.{" "}
            <Link
              to="/runs"
              className="font-medium text-indigo-600 hover:text-indigo-700"
            >
              Live runs view
            </Link>{" "}
            shows the same data with a 5 s polling cadence and fewer filters.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => runs.refetch()}
          className="gap-2"
          data-testid="history-refresh"
        >
          <RefreshCw size={14} />
          Refresh
        </Button>
      </header>

      <section
        className="grid grid-cols-1 gap-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-3 lg:grid-cols-6"
        data-testid="history-filters"
      >
        <Field label="Workflow">
          <Select value={workflowFilter} onValueChange={setWorkflowFilter}>
            <SelectTrigger data-testid="history-filter-workflow">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All workflows</SelectItem>
              {(workflows.data?.items || []).map((w) => (
                <SelectItem
                  key={w.workflow_id}
                  value={w.workflow_id}
                  data-testid={`history-filter-workflow-${w.slug}`}
                >
                  {w.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Status">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="history-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Date range">
          <Select value={datePreset} onValueChange={setDatePreset}>
            <SelectTrigger data-testid="history-filter-date">
              <CalendarDays size={13} className="text-slate-500" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATE_PRESETS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Tag">
          <Select value={tagFilter} onValueChange={setTagFilter}>
            <SelectTrigger data-testid="history-filter-tag">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Any tag</SelectItem>
              {TAG_CHOICES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Triggered by">
          <Input
            placeholder="email@eyvo.com"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
            data-testid="history-filter-email"
          />
        </Field>

        <Field label="Group by workflow">
          <div className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2 text-sm">
            <span className="text-slate-600">
              {groupByWorkflow ? "On" : "Off"}
            </span>
            <Switch
              checked={groupByWorkflow}
              onCheckedChange={setGroupByWorkflow}
              data-testid="history-group-toggle"
            />
          </div>
        </Field>
      </section>

      <section className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
        {runs.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : runs.isError ? (
          <div
            className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700"
            data-testid="history-error"
          >
            {formatApiError(runs.error)}
          </div>
        ) : filteredRuns.length === 0 ? (
          <div
            className="px-6 py-16 text-center text-sm text-slate-500"
            data-testid="history-empty"
          >
            No runs match your current filters. Try widening the date range
            or clearing tag/status filters.
          </div>
        ) : groupByWorkflow ? (
          <div data-testid="history-grouped">
            {grouped.map((g) => (
              <div key={g.workflow_id} className="border-b border-slate-100 last:border-0">
                <div className="bg-slate-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600">
                  {g.workflow_name}{" "}
                  <span className="font-normal text-slate-400">
                    · {g.rows.length} run{g.rows.length !== 1 ? "s" : ""}
                  </span>
                </div>
                <RunsTable
                  rows={g.rows}
                  onRerun={(r) => rerunMutation.mutate(r.run_id)}
                  onDuplicate={(r) => duplicateMutation.mutate(r.run_id)}
                  onExport={(r) => handleExport(r.run_id)}
                  canRun={canRun}
                  canDuplicate={canDuplicate}
                  isRerunPending={rerunMutation.isPending}
                  isDuplicatePending={duplicateMutation.isPending}
                />
              </div>
            ))}
          </div>
        ) : (
          <RunsTable
            rows={filteredRuns}
            onRerun={(r) => rerunMutation.mutate(r.run_id)}
            onDuplicate={(r) => duplicateMutation.mutate(r.run_id)}
            onExport={(r) => handleExport(r.run_id)}
            canRun={canRun}
            canDuplicate={canDuplicate}
            isRerunPending={rerunMutation.isPending}
            isDuplicatePending={duplicateMutation.isPending}
          />
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </div>
      {children}
    </div>
  );
}

function RunsTable({
  rows,
  onRerun,
  onDuplicate,
  onExport,
  canRun,
  canDuplicate,
  isRerunPending,
  isDuplicatePending,
}) {
  const runIds = useMemo(() => rows.map((r) => r.run_id), [rows]);
  const folderSummaryQuery = useFolderSummary(runIds);
  const folderSummary = folderSummaryQuery.data || {};
  return (
    <Table data-testid="history-table">
      <TableHeader className="bg-slate-50">
        <TableRow>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">Run #</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">Workflow</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">v</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">Started</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">Duration</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">Triggered by</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">Steps</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">Narration</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500">📁</TableHead>
          <TableHead className="text-xs uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r) => (
          <TableRow key={r.run_id} data-testid={`history-row-${r.run_id}`}>
            <TableCell className="font-mono text-xs text-indigo-700" data-testid={`history-row-number-${r.run_id}`}>
              #{formatRunNumber(r.run_number)}
            </TableCell>
            <TableCell>
              <Link
                to={`/runs/${r.run_id}`}
                className="font-medium text-slate-800 hover:text-indigo-600"
              >
                {r.workflow_name_snapshot}
              </Link>
            </TableCell>
            <TableCell className="font-mono text-xs text-slate-500">v{r.workflow_version}</TableCell>
            <TableCell><RunStatusPill status={r.status} /></TableCell>
            <TableCell className="text-sm text-slate-600">{formatDate(r.started_at)}</TableCell>
            <TableCell className="text-sm text-slate-600">{formatDuration(r.duration_ms)}</TableCell>
            <TableCell className="font-mono text-xs text-slate-500">{r.triggered_by}</TableCell>
            <TableCell className="font-mono text-xs text-slate-600">{r.completed_steps}/{r.total_steps}</TableCell>
            <TableCell><NarrationPill status={r.narration_status} /></TableCell>
            <TableCell>
              <AddToFolderDropdown runId={r.run_id}>
                <button
                  type="button"
                  className="cursor-pointer transition-opacity hover:opacity-80"
                  aria-label="Manage folders for this run"
                  data-testid={`history-folder-trigger-${r.run_id}`}
                >
                  <FolderCountBadge summary={folderSummary?.[r.run_id]} runId={r.run_id} />
                </button>
              </AddToFolderDropdown>
            </TableCell>
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-1">
                {canRun && r.status === "completed" && (
                  <button
                    type="button"
                    onClick={() => onRerun(r)}
                    disabled={isRerunPending}
                    className="rounded-md p-1.5 text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
                    title="Rerun with same workflow_version + variables"
                    data-testid={`history-rerun-${r.run_id}`}
                  >
                    <PlayCircle size={15} />
                  </button>
                )}
                {canDuplicate && (
                  <button
                    type="button"
                    onClick={() => onDuplicate(r)}
                    disabled={isDuplicatePending}
                    className="rounded-md p-1.5 text-indigo-600 hover:bg-indigo-50 disabled:opacity-40"
                    title="Duplicate the pinned workflow version into a new workflow"
                    data-testid={`history-duplicate-${r.run_id}`}
                  >
                    <Copy size={15} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onExport(r)}
                  className="rounded-md p-1.5 text-slate-600 hover:bg-slate-100"
                  title="Export JSON bundle"
                  data-testid={`history-export-${r.run_id}`}
                >
                  <Download size={15} />
                </button>
                <Link
                  to={`/runs/${r.run_id}`}
                  className="ml-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
                  data-testid={`history-open-${r.run_id}`}
                >
                  Open →
                </Link>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function NarrationPill({ status }) {
  if (status === "ready") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700"
        data-testid="narration-pill-ready"
      >
        <Sparkles size={9} />
        Ready
      </span>
    );
  }
  if (status === "generating") {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-medium text-indigo-700"
        data-testid="narration-pill-generating"
      >
        <Loader2 size={9} className="animate-spin" />
        Generating
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center rounded-full bg-rose-50 px-2 py-0.5 text-[10px] font-medium text-rose-700">
        Failed
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-500"
      data-testid="narration-pill-none"
    >
      —
    </span>
  );
}
